import React from "react";
import { SEVERITY_CLASS, humanise } from "../constants";

// One saved reading, displayed. Pure: no fetching, no state - so the viewer tab and anything else that shows
// a reading render it identically.

const PROMINENCE_ORDER = { primary: 0, secondary: 1, background: 2 };

export function ObjectChips({ objects }) {
    if (!objects || !objects.length) return null;
    // Primary first, then secondary, then background: the order a person reads the photo in, which is not
    // necessarily the order the model returned.
    const sorted = [...objects].sort((a, b) => (PROMINENCE_ORDER[a.prominence] ?? 3) - (PROMINENCE_ORDER[b.prominence] ?? 3));
    return (
        <div className="ia-chips">
            {sorted.map((o, i) => (
                <span
                    key={`${o.name}-${i}`}
                    className={`ia-chip ia-prom-${o.prominence}`}
                    title={`${humanise(o.prominence)} · ${humanise(o.confidence)} confidence`}
                >
                    {o.name}
                    {o.confidence === "low" && <i className="ia-chip-low">?</i>}
                </span>
            ))}
        </div>
    );
}

export function VisibleText({ items }) {
    if (!items || !items.length) return null;
    // Quoted and in a monospaced face: this is a TRANSCRIPTION, and a room number or a panel label read
    // off a photo has to be visibly distinct from Otto's own words about the photo. "LP-2" and LP-2 are
    // different claims.
    return (
        <ul className="ia-text-list">
            {items.map((item, i) => (
                <li key={`${item.text}-${i}`}>
                    <span className="ia-text-value">&ldquo;{item.text}&rdquo;</span>
                    <span className="ia-text-kind">{humanise(item.kind)}</span>
                    {item.subject && <span className="ia-on"> on {item.subject}</span>}
                </li>
            ))}
        </ul>
    );
}

export function Defects({ defects }) {
    if (!defects || !defects.length) {
        return <div className="ia-nodefects">No visible deficiencies</div>;
    }
    return (
        <ul className="ia-defects">
            {defects.map((d, i) => (
                <li key={`${d.kind}-${i}`}>
                    <span className={`ia-sev ${SEVERITY_CLASS[d.severity] || ""}`}>{humanise(d.severity)}</span>
                    <strong>{d.kind}</strong>
                    <span className="ia-on"> on {d.subject}</span>
                    {d.evidence && <div className="ia-evidence">{d.evidence}</div>}
                </li>
            ))}
        </ul>
    );
}

function AnalysisReading({ analysis }) {
    if (!analysis) return null;
    return (
        <div className="ia-reading">
            <div className="ia-caption">{analysis.caption}</div>
            <div className="ia-section-label">Primary subject and scene</div>
            <div className="ia-scene">
                <span className="ia-pill ia-pill-primary">{analysis.primary_subject}</span>
                <span className="ia-pill">{humanise(analysis.setting)}</span>
                <span className="ia-pill">{humanise(analysis.space_type)}</span>
                {analysis.quality && analysis.quality !== "usable" && (
                    <span className="ia-pill ia-pill-warn">{humanise(analysis.quality)}</span>
                )}
            </div>
            <div className="ia-section-label">Objects</div>
            <ObjectChips objects={analysis.objects} />
            <div className="ia-section-label">Visible deficiencies</div>
            <Defects defects={analysis.defects} />
            {analysis.visible_text && analysis.visible_text.length > 0 && (
                <>
                    <div className="ia-section-label">Text in the photo</div>
                    <VisibleText items={analysis.visible_text} />
                </>
            )}
            {analysis.other_objects && analysis.other_objects.length > 0 && (
                <div className="ia-other">Also noted: {analysis.other_objects.join(", ")}</div>
            )}
        </div>
    );
}

export default AnalysisReading;
